import { inject, Injectable } from '@angular/core';
import { type IconDefinition } from '@fortawesome/angular-fontawesome';
import {
  faFileArchive,
  faFileExcel,
  faFilePdf,
  faFilePowerpoint,
  faFileWord,
  faPaperclip,
} from '@fortawesome/pro-regular-svg-icons';
import { type AttachedFile } from '@reconcept-platform/shared/types';
import { Observable, of, ReplaySubject } from 'rxjs';
import { UtilService } from '../services/util.service';

export interface FileThumbnailData {
  contentType?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  url?: string | null;
  previewUrl?: string | null; //server side generated preview image
  previewDataURL?: string | null; //in memory base64 url only if image
}

export enum SimpleFileTypeEnum {
  word = 'word',
  excel = 'excel',
  powerpoint = 'powerpoint',
  zip = 'zip',
  pdf = 'pdf',
  image = 'image',
}

export enum UploadFileStatusEnum {
  existing = 'EXISTING',
  processing = 'PROCESSING',
  uploading = 'UPLOADING',
  uploaded = 'UPLOADED',
  error = 'ERROR',
}

// our map of mime-types to a simple string representing common file types
const contentTypesMap: { [contentType: string]: SimpleFileTypeEnum } = {
  'application/msword': SimpleFileTypeEnum.word,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': SimpleFileTypeEnum.word,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template': SimpleFileTypeEnum.word,
  'application/vnd.ms-word.document.macroEnabled.12': SimpleFileTypeEnum.word,
  'application/vnd.ms-excel': SimpleFileTypeEnum.excel,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': SimpleFileTypeEnum.excel,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template': SimpleFileTypeEnum.excel,
  'application/vnd.ms-excel.sheet.macroEnabled.12': SimpleFileTypeEnum.excel,
  'application/vnd.ms-excel.template.macroEnabled.12': SimpleFileTypeEnum.excel,
  'application/vnd.ms-excel.addin.macroEnabled.12': SimpleFileTypeEnum.excel,
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12': SimpleFileTypeEnum.excel,
  'application/vnd.ms-powerpoint': SimpleFileTypeEnum.powerpoint,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': SimpleFileTypeEnum.powerpoint,
  'application/vnd.openxmlformats-officedocument.presentationml.template': SimpleFileTypeEnum.powerpoint,
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow': SimpleFileTypeEnum.powerpoint,
  'application/vnd.ms-powerpoint.addin.macroEnabled.12': SimpleFileTypeEnum.powerpoint,
  'application/vnd.ms-powerpoint.presentation.macroEnabled.12': SimpleFileTypeEnum.powerpoint,
  'application/vnd.ms-powerpoint.template.macroEnabled.12': SimpleFileTypeEnum.powerpoint,
  'application/vnd.ms-powerpoint.slideshow.macroEnabled.12': SimpleFileTypeEnum.powerpoint,
  'application/pdf': SimpleFileTypeEnum.pdf,
  'x-pdf': SimpleFileTypeEnum.pdf,
  'application/x-rar-compressed': SimpleFileTypeEnum.zip,
  'application/octet-stream': SimpleFileTypeEnum.zip,
  'application/zip': SimpleFileTypeEnum.zip,
  'application/x-zip-compressed': SimpleFileTypeEnum.zip,
  'multipart/x-zip': SimpleFileTypeEnum.zip,
  'image/bmp': SimpleFileTypeEnum.image,
  'image/png': SimpleFileTypeEnum.image,
  'image/jpeg': SimpleFileTypeEnum.image,
  'image/gif': SimpleFileTypeEnum.image,
  'image/svg+xml': SimpleFileTypeEnum.image,
};

/**
 * FileReader is patched by both CapacitorJS & ZoneJS
 * This combination does not work well together, so we return the ZoneJS instance if available.
 *
 * https://github.com/ionic-team/capacitor/issues/1564
 */
export function getFileReader(): FileReader {
  const fileReader = new FileReader();
  const zoneOriginalInstance = (fileReader as any)['__zone_symbol__originalInstance'];
  return zoneOriginalInstance || fileReader;
}

export interface UploadableFile {
  fileBlob: File;
  fileData: FileThumbnailData;
  directUploadId?: string;

  hasError(): boolean;
  setError(errorMessage: string): void;
  setStatus(status: UploadFileStatusEnum): void;
  setUploadProgress(percentage: number): void;
}

/**
 * @deprecated Use `SelectedFile` from `@reconcept-platform/shared/forms/web` instead
 */
export class SelectedFile implements UploadableFile {
  id: string; //optional id that corresponds with a server-side object
  directUploadId: string; //id of the direct upload created
  fileData: FileThumbnailData = {};
  fileBlob: File;

  status: UploadFileStatusEnum;
  errorMessage: string; // error message if status is ERROR
  willBeRemoved: boolean;
  uploadProgress: number; // 0 - 1
  updates$: ReplaySubject<SelectedFile> = new ReplaySubject();

  constructor(data?: {
    id?: string;
    fileData?: FileThumbnailData;
    fileBlob?: File;
    status?: UploadFileStatusEnum;
    willBeRemoved?: boolean;
  }) {
    if (data) {
      this.setData(data);
    }
  }

  hasError() {
    return this.status === UploadFileStatusEnum.error;
  }

  get isProcessing() {
    return (
      this.status === UploadFileStatusEnum.processing ||
      this.status === UploadFileStatusEnum.uploading ||
      (this.uploadProgress > 0 && this.uploadProgress < 1)
    );
  }

  get hasFile(): boolean {
    return !!this.fileData?.fileSize || !!this.fileData?.url;
  }

  setData(data: {
    id?: string;
    fileData?: FileThumbnailData;
    fileBlob?: File;
    status?: UploadFileStatusEnum;
    willBeRemoved?: boolean;
  }) {
    // @ts-expect-error TS2322 type not assignable
    this.id = data.id;
    // @ts-expect-error TS2322 type not assignable
    this.willBeRemoved = data.willBeRemoved;
    this.fileData = data.fileData || {};
    // @ts-expect-error TS2322 type not assignable
    this.fileBlob = data.fileBlob;
    if (data.status) {
      this.setStatus(data.status);
    } else {
      this.emitUpdate();
    }
  }

  reset() {
    this.fileData = {};
    // @ts-expect-error TS2322 type not assignable
    this.fileBlob = null;
    // @ts-expect-error TS2322 type not assignable
    this.status = null;
    // @ts-expect-error TS2322 type not assignable
    this.willBeRemoved = null;
    // @ts-expect-error TS2322 type not assignable
    this.errorMessage = null;
    this.emitUpdate();
  }

  setStatus(status: UploadFileStatusEnum) {
    this.status = status;
    this.emitUpdate();
  }

  setError(errorMessage: string) {
    this.setStatus(UploadFileStatusEnum.error);
    this.errorMessage = errorMessage;
    this.emitUpdate();
  }

  setUploadProgress(progress: number) {
    this.uploadProgress = progress;
    this.emitUpdate();
  }

  private emitUpdate() {
    this.updates$.next(this);
  }
}

export function mapAttachedFiles(attachedFiles: AttachedFile[]) {
  return attachedFiles?.map((attachedFile) => {
    return new SelectedFile({
      id: attachedFile.id,
      status: UploadFileStatusEnum.existing,
      fileData: {
        contentType: attachedFile.contentType,
        fileName: attachedFile.fileName,
        fileSize: attachedFile.fileSize,
        url: attachedFile.url,
      },
    });
  });
}

@Injectable({
  providedIn: 'root',
})
export class FileUtilService {
  private readonly utilService = inject(UtilService);

  getFileTypeByContentType(contentType: string): SimpleFileTypeEnum {
    return contentTypesMap[contentType];
  }

  getFilePreviewUrl(contentType: string, previewUrl: string) {
    if (!contentType) {
      return null;
    }

    // only applies to images
    if (contentType.indexOf('image') === -1) {
      return null;
    }

    if (previewUrl) {
      return `url("${previewUrl}")`;
    } else {
      return null;
    }
  }

  getStyling(contentType: string): {
    fileTypeIcon: IconDefinition;
    fileTypeColorLegacyClass:
      'file-color-default' | 'file-color-red' | 'file-color-green' | 'file-color-yellow' | 'file-color-blue';
    fileTypeColor: string;
    fileTypeBackgroundColor: string;
  } {
    switch (this.getFileTypeByContentType(contentType)) {
      case SimpleFileTypeEnum.word: {
        return {
          fileTypeIcon: faFileWord,
          fileTypeColorLegacyClass: 'file-color-blue',
          fileTypeColor: 'var(--col-primary)',
          fileTypeBackgroundColor: 'var(--col-primary-bg)',
        };
      }
      case SimpleFileTypeEnum.excel: {
        return {
          fileTypeIcon: faFileExcel,
          fileTypeColorLegacyClass: 'file-color-green',
          fileTypeColor: 'var(--col-positive-dark)',
          fileTypeBackgroundColor: 'var(--col-positive-bg)',
        };
      }
      case SimpleFileTypeEnum.powerpoint: {
        return {
          fileTypeIcon: faFilePowerpoint,
          fileTypeColorLegacyClass: 'file-color-yellow',
          fileTypeColor: 'var(--col-severe-dark)',
          fileTypeBackgroundColor: 'var(--col-severe-bg)',
        };
      }
      case SimpleFileTypeEnum.pdf: {
        return {
          fileTypeIcon: faFilePdf,
          fileTypeColorLegacyClass: 'file-color-red',
          fileTypeColor: 'var(--col-negative-dark)',
          fileTypeBackgroundColor: 'var(--col-negative-bg)',
        };
      }
      case SimpleFileTypeEnum.zip: {
        return {
          fileTypeIcon: faFileArchive,
          fileTypeColorLegacyClass: 'file-color-default',
          fileTypeColor: 'var(--col-text-secondary)',
          fileTypeBackgroundColor: 'var(--col-bg-main)',
        };
      }
      default: {
        return {
          fileTypeIcon: faPaperclip,
          fileTypeColorLegacyClass: 'file-color-default',
          fileTypeColor: 'var(--col-text-secondary)',
          fileTypeBackgroundColor: 'var(--col-bg-main)',
        };
      }
    }
  }

  // get base 64 file preview if the type is image
  getFileImagePreview(fileBlob: File): Observable<string> {
    // only continue if type is image
    if (fileBlob.type.indexOf('image') === -1) {
      // @ts-expect-error TS2322 type not assignable
      return of(null);
    }

    const fileReader = getFileReader();

    return Observable.create((observer: any) => {
      // on load of a chunk
      fileReader.onload = function ($event) {
        observer.next((<any>$event.target).result);
        observer.complete();
      };

      // if an error occurs
      fileReader.onerror = (error) => observer.error(error);

      fileReader.readAsDataURL(fileBlob);
    });
  }

  isOfficeFile(contentType: string): boolean {
    //https://technet.microsoft.com/en-us/library/ee309278(office.12).aspx
    const officeMimeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
      'application/vnd.ms-word.document.macroEnabled.12',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
      'application/vnd.ms-excel.template.macroEnabled.12',
      'application/vnd.ms-excel.addin.macroEnabled.12',
      'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.template',
      'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
      'application/vnd.ms-powerpoint.addin.macroEnabled.12',
      'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
      'application/vnd.ms-powerpoint.template.macroEnabled.12',
      'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
      'application/vnd.ms-access',
      'application/x-ole-storage', //sometimes stored as this type by accident(?)
    ];
    return officeMimeTypes.indexOf(contentType) > -1;
  }

  isImageFile(contentType: string, fileNameOrUrl: string): boolean {
    const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg'];
    // @ts-expect-error TS2322 type not assignable
    return (
      imageExtensions.indexOf(this.getExtension(fileNameOrUrl)) > -1 ||
      (contentType && contentType.indexOf('image') > -1)
    );
  }

  isPDF(fileNameOrUrl: string): boolean {
    return this.getExtension(fileNameOrUrl) === 'pdf';
  }

  isAudioFile(contentType: string): boolean {
    return contentType.includes('audio/');
  }

  isVideoFile(contentType: string): boolean {
    return contentType.includes('video/');
  }

  getPreviewFileURL(fileURL: string, contentType: string) {
    return this.isOfficeFile(contentType)
      ? 'https://view.officeapps.live.com/op/view.aspx?src=' + encodeURIComponent(fileURL)
      : fileURL;
  }

  selectFile(options?: { accept: SimpleFileTypeEnum[]; capture?: 'user' | 'environment' }): Promise<File | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = this.getAccept(...(options?.accept ?? [])) ?? '';
      if (options?.capture) {
        input.capture = options.capture;

        // Android's file chooser only recognizes the literal "image/*" (not specific image mime types)
        // to decide whether to offer camera capture; this doesn't matter on other platforms, so scope it to Android
        if (this.utilService.isAndroid() && options.accept.includes(SimpleFileTypeEnum.image)) {
          const accept = input.accept.split(',');
          if (!accept.includes('image/*')) {
            accept.push('image/*');
          }

          input.accept = accept.join(',');
        }
      }

      input.style.display = 'none';
      document.body.appendChild(input);

      input.addEventListener(
        'change',
        () => {
          resolve(input.files?.[0] ?? null);
          input.remove();
        },
        { once: true },
      );

      input.click();
    });
  }

  getAccept(...wantedFileTypes: SimpleFileTypeEnum[]) {
    if (!wantedFileTypes.length) {
      return null;
    }

    const acceptedMime = [];

    for (const [mime, fileType] of Object.entries(contentTypesMap)) {
      if (wantedFileTypes.includes(fileType)) {
        acceptedMime.push(mime);
      }
    }

    return acceptedMime.join(',');
  }

  getWordedSize(bytes: number, decimals = 2) {
    if (!bytes) return '0B';

    const k = 1024;
    const sizes = ['B', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))}${sizes[i]}`;
  }

  private getExtension(fileURL: string) {
    const trailing = fileURL.split('.').pop();
    // @ts-expect-error TS18048 possibly undefined
    return trailing.split('?')[0].toLowerCase();
  }
}
